import { AxiosRequestConfig } from 'axios'
import { MasterExercise, MasterExerciseLookups, MasterExerciseIdentifier } from 'src/types/MasterExercise'
import {
	Api_GetMasterWorkoutsRequest,
	API_GetMasterWorkoutsResponse,
	API_ImportMasterWorkoutRequest,
	MasterWorkout,
	MasterWorkoutMax,
	MasterWorkoutLookups,
	MasterWorkoutValidationResponse,
} from 'src/types/MasterWorkout'
import { API_SubmitMasterWorkoutExercisesRequest, MasterWorkoutExercise, MasterWorkoutExerciseLookups } from 'src/types/MasterWorkoutExercise'
import { PaginatedResult } from 'src/types/PaginatedResult'
import { appUrls } from 'src/utils/ajax'
import { HttpClient } from './http-client-interceptor'
import { toast } from 'react-toastify';

class ApiService extends HttpClient {
	private static _instance: ApiService

	public constructor() {
		super(String(appUrls.azure_api))
	}

	public static get instance() {
		return this._instance ?? (this._instance = new ApiService())
	}
	/**
	 * ************************************************
	 *  MasterExercises
	 * ************************************************
	 */

	public getMasterExercises = async (pageNum: number, searchText: string, sortPhrase: string, sortDirection: number): Promise<PaginatedResult<MasterExercise>> => {
		const config: AxiosRequestConfig = { params: { pageNum, searchText, sortPhrase, sortDirection } }
		const result = await this.get<PaginatedResult<MasterExercise>>(`/admin/master-exercises`, config)
		console.log('@ApiService->getMasterExercises', result)
		return result
	}

	public getMasterExercise = async (id: number | string): Promise<MasterExercise> => {
		const result = await this.get<MasterExercise>(`/admin/master-exercises/${id}`)
		console.log('@ApiService->getMasterExercise', result)
		return result
	}

	public getMasterExerciseLookups = async (): Promise<MasterExerciseLookups> => {
		const result = await this.get<MasterExerciseLookups>(`/admin/master-exercises/lookups`)
		console.log('@ApiService->getMasterExerciseLookups', result)
		return result
	}

	public submitMasterExercise = async (value: MasterExercise): Promise<number> => {
		const result = await this.post<number>(`/admin/master-exercises`, value)
		console.log('@ApiService->submitMasterExercise', result)
		return result
	}

	public deleteMasterExercises = async (deletedIds: number[]): Promise<boolean> => {
		const config: AxiosRequestConfig = { params: { deletedIds: deletedIds } }
		const result = await this.delete<boolean>(`/admin/master-exercises`, config)
		console.log('@ApiService->deleteMasterExercises', result)
		return result
	}

	public fetchVimeoThumbnailForMasterExercise = async (value: MasterExerciseIdentifier): Promise<string> => {
		const result = await this.post<string>(`/vimeo/thumbnails`, value)
		console.log('@ApiService->fetchVimeoThumbnailForMasterExercise', result)
		return result
	}

	public addMasterExerciseFilters = async (ids: number[], filterIds: number[]): Promise<number[]> => {
		const data = { ids: ids, masterExerciseGroupFilterTermIds: filterIds }
		const result = await this.post<number[]>(`/admin/master-exercises/filters-add`, data)
		console.log('@ApiService->addMasterExerciseFilters', result)
		return result
	}

	public removeMasterExerciseFilters = async (ids: number[], filterIds: number[]): Promise<number[]> => {
		const data = { ids: ids, masterExerciseGroupFilterTermIds: filterIds }
		const result = await this.post<number[]>(`/admin/master-exercises/filters-remove`, data)
		console.log('@ApiService->removeMasterExerciseFilters', result)
		return result
	}

	/**
	 * ************************************************
	 *  MasterWorkouts
	 * ************************************************
	 */

	public getMasterWorkout = async (id: number | string): Promise<MasterWorkout> => {
		const result = await this.get<MasterWorkout>(`/admin/master-workouts/${id}`)
		console.log('@ApiService->getMasterWorkout', result)
		return result
	}
	public getMasterWorkoutForExport = async (id: number | string): Promise<string> => {
		const result = await this.get<string>(`/admin/master-workouts/export/${id}`)
		console.log('@ApiService->getMasterWorkoutForExport', result)
		return result
	}

	public getMasterWorkouts = async (params?: Api_GetMasterWorkoutsRequest): Promise<API_GetMasterWorkoutsResponse> => {
		const config: AxiosRequestConfig = { params: params }
		const result = await this.get<API_GetMasterWorkoutsResponse>(`/master-workouts`, config)
		console.log('@ApiService->getMasterWorkouts', result)
		return result
	}

	public getMasterWorkoutLookups = async (): Promise<MasterWorkoutLookups> => {
		const result = await this.get<MasterWorkoutLookups>(`/admin/master-workouts/lookups`)
		console.log('@ApiService->getMasterWorkoutLookups', result)
		return result
	}
	
	public submitMasterWorkout = async (value: MasterWorkout): Promise<number> => {
		const result = await this.post<number>(`/admin/master-workouts`, value)
		console.log('@ApiService->submitMasterWorkout', result)
		return result
	}

	public submitMasterWorkoutMaxes = async (value: { masterWorkoutId: number; masterWorkoutMaxes: MasterWorkoutMax[] }): Promise<number> => {
		const result = await this.post<number>('/admin/master-workout-maxes', value);
		console.log('@ApiService->submitMasterWorkoutMaxes', result)
		return result
	}

	public importMasterWorkout = async (obj: API_ImportMasterWorkoutRequest): Promise<MasterWorkoutValidationResponse> => {
		const result = await this.post<MasterWorkoutValidationResponse>(`/admin/master-workouts/import`, obj)
		console.log('@ApiService->importMasterWorkout', result)
		return result
	}

	public validateMasterWorkout = async (obj: API_ImportMasterWorkoutRequest): Promise<MasterWorkoutValidationResponse> => {
		const result = await this.post<MasterWorkoutValidationResponse>(`/admin/master-workouts/validate-import`, obj)
		console.log('@ApiService->validateMasterWorkout', result)
		return result
	}

	public deleteMasterWorkout = async (id: number): Promise<boolean> => {
		const result = await this.delete<boolean>(`/admin/master-workouts/${id}`)
		console.log('@ApiService->deleteMasterWorkout', result)
		return result
	}

	/**
	 * ************************************************
	 *  MasterWorkoutExercises
	 * ************************************************
	 */

	public getMasterWorkoutExercise = async (id: number | string): Promise<MasterWorkoutExercise> => {
		const result = await this.get<MasterWorkoutExercise>(`/admin/master-workout-exercises/${id}`)
		console.log('@ApiService->getMasterWorkoutExercise', result)
		return result
	}

	public getMasterWorkoutExercises = async (masterWorkoutId: number | string): Promise<MasterWorkoutExercise[]> => {
		const result = await this.get<MasterWorkoutExercise[]>(`/admin/master-workout-exercises/list/${masterWorkoutId}`)
		console.log('@ApiService->getMasterWorkoutExercises', result)
		return result
	}

	public getMasterWorkoutExerciseLookups = async (): Promise<MasterWorkoutExerciseLookups> => {
		const result = await this.get<MasterWorkoutExerciseLookups>(`/admin/master-workout-exercises/lookups`)
		console.log('@ApiService->getMasterWorkoutExerciseLookups', result)
		return result
	}

	// Create or Update single master workout exercise
	public submitMasterWorkoutExercise = async (value: MasterWorkoutExercise): Promise<number> => {
		const promise = this.post<number>(`/admin/master-workout-exercises`, value, 'MasterWorkoutExercise_Page')
		const result = await toast.promise(
			promise,
			{
				pending: 'Saving',
				success: 'Successfully saved',
				error: 'Something broke',
			},
			{
				toastId: `submitMasterWorkoutExercise`,
				autoClose: 1500,
				position: 'top-center'
			},
		)

		console.log('@ApiService->submitMasterWorkoutExercise', result)
		return result
	}
	
	// Create or Update or Delete master workout exercises
	public submitMasterWorkoutExercises = async (value: API_SubmitMasterWorkoutExercisesRequest): Promise<boolean> => {
		const result = await this.post<boolean>(`/admin/master-workout-exercises/createOrEditOrDelete`, value)
		console.log('@ApiService->submitMasterWorkoutExercise', result)
		return result
	}

	public deleteMasterWorkoutExercises = async (deletedIds: number[]): Promise<boolean> => {
		if (!deletedIds?.length) return
		
		const config: AxiosRequestConfig = { params: { deletedIds: deletedIds } }
		const promise = this.delete<boolean>(`/admin/master-workout-exercises`, config, 'MasterWorkoutExercise_Page')
		const result = await toast.promise(
			promise,
			{
				pending: 'Saving',
				success: 'Successfully saved',
				error: 'Something broke',
			},
			{
				toastId: `submitMasterWorkoutExercise`,
				autoClose: 1500,
				position: 'top-center'
			},
		)
		console.log('@ApiService->deleteMasterWorkoutExercises', result)
		return result
	}

	/**
	 * ************************************************
	 *  Cache
	 * ************************************************
	 */
	public clearCache = (): Promise<any> => {
		console.log('@ApiService->clearCache')
		return this.delete<any>(`/admin/clear-cache`)
	}

	public clearMasterWorkoutExercisesCache = (masterWorkoutId: number): Promise<any> => {
		console.log(`@ApiService->clearMasterWorkoutExercisesCache/${masterWorkoutId}`)
		return this.delete<any>(`/admin/clear-cache/master-workout-exercises/${masterWorkoutId}`)
	}

	/**
	 * ************************************************
	 *  Permissions
	 * ************************************************
	 */

	public getAdminPermissions = async (): Promise<{
		userId: number
		isAdmin: boolean
		isAuthor: boolean
		isWbAdmin: boolean
		isWbAuthor: boolean
	}> => {
		const permissions = await this.get<{
			userId: number
			isAdmin: boolean
			isAuthor: boolean
			isWbAdmin: boolean
			isWbAuthor: boolean
		}>('/admin/permissions')
		console.log(`@ApiService->getAdminPermissions`, permissions)
		return permissions
	}
}

export default ApiService
